import { Button, Classes } from '@blueprintjs/core';
import * as React from 'react';
import { isEqual } from 'vet-bones/utils';

import { PropertyTypeSelector } from 'src/components/PropertyTypeSelector';
import { createNewSelectItemRenderer } from 'src/components/select/createNewSelectItemRenderer';
import { SelectableLinks } from 'src/components/SelectableLinks';
import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE,
  PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE,
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectLinkTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { ProjectDiagramEditableIri } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableIri';
import { ProjectDiagramEditableTextArea } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableTextArea';
import { ProjectDiagramRelationshipDrawerTable } from 'src/containers/ProjectDiagram/components/relationshipDrawer/ProjectDiagramRelationshipDrawerTable';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import { useCreateModelConceptClassFromQuery } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useCreateModelConceptClassFromQuery';
import {
  useAddClassForRelationship,
  useRemoveClassForRelationship,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptClassForRelationship';
import { useUpdateDescriptionForIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateDescriptionForIri';
import { useUpdateIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateIri';
import { useUpdateLabel } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateLabel';
import { useUpdatePropertyType } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdatePropertyType';
import { useRemoveModelConceptRelationship } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveModelConceptRelationship';
import { ModelConceptRelationship } from 'src/containers/ProjectDiagram/types/data';
import { relationshipDraftChangesSelector } from 'src/selectors/draftRelationship';
import { entityDrawerLinkDataSelector } from 'src/selectors/entityDrawerLinkData';
import { projectClassItemsSelector } from 'src/selectors/projectClassItems';
import { projectDescriptionsByIriSelector } from 'src/selectors/projectDescriptionsByIri';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectRelationshipDomainAndRangesSelector } from 'src/selectors/projectRelationshipDomainAndRanges';
import { propertyTypeByIriSelector } from 'src/selectors/propertyType';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectDiagramRelationshipDrawer: React.VFC = () => {
  const { projectId } = useRouterParams();

  const labelsByIri = useReduxSelector(
    projectLabelsByIriSelector(projectId),
    isEqual
  );

  const descriptionsByIri = useReduxSelector(
    projectDescriptionsByIriSelector(projectId),
    isEqual
  );

  const linkData = useReduxSelector(
    entityDrawerLinkDataSelector<ModelConceptRelationship>(projectId),
    isEqual
  );

  const { isCustomPropertyType, propertyType } = useReduxSelector(
    propertyTypeByIriSelector(projectId, linkData?.value)
  );

  const currentIris = useReduxSelector(
    projectRelationshipDomainAndRangesSelector(projectId, linkData?.value || '')
  );

  const allClassItems = useReduxSelector(
    projectClassItemsSelector(projectId),
    isEqual
  );

  const { isDescriptionModified, isLabelModified, addedSources, addedTargets } =
    useReduxSelector(
      relationshipDraftChangesSelector(projectId, linkData?.value),
      isEqual
    );

  const domainAndRangeSelectItems = React.useMemo(() => {
    return {
      currentDomains: allClassItems.filter((item) =>
        currentIris.domains.has(item.value)
      ),
      availableDomains: allClassItems.filter(
        (item) => !currentIris.domains.has(item.value)
      ),
      currentRanges: allClassItems.filter((item) =>
        currentIris.ranges.has(item.value)
      ),
      availableRanges: allClassItems.filter(
        (item) => !currentIris.ranges.has(item.value)
      ),
    };
  }, [allClassItems, currentIris]);

  const handleUpdateIri = useUpdateIri(linkData?.value);

  const handleUpdateLabel = useUpdateLabel(linkData?.value);

  const handleUpdateDescription = useUpdateDescriptionForIri(linkData?.value);

  const handleUpdatePropertyType = useUpdatePropertyType(linkData?.value);

  const handleRemoveRelationship = useRemoveModelConceptRelationship();

  const handleAddDomain = useAddClassForRelationship({
    direction: RelationshipDirection.OUTGOING,
    relationshipIri: linkData?.value,
  });
  const handleAddRange = useAddClassForRelationship({
    direction: RelationshipDirection.INCOMING,
    relationshipIri: linkData?.value,
  });

  const handleRemoveDomain = useRemoveClassForRelationship({
    direction: RelationshipDirection.OUTGOING,
    relationshipIri: linkData?.value,
  });
  const handleRemoveRange = useRemoveClassForRelationship({
    direction: RelationshipDirection.INCOMING,
    relationshipIri: linkData?.value,
  });

  const handleClickClass = useClickModelConceptClassLink();

  const createNewClassFromQuery = useCreateModelConceptClassFromQuery();

  if (!linkData) {
    return null;
  }

  const label = labelsByIri[linkData.value];

  const descriptionForIri = descriptionsByIri[linkData.value] || '';

  return (
    <>
      <div className={Classes.DRAWER_BODY}>
        <ProjectDiagramEditableIri
          header={copy.components.projectDiagram.relationshipDrawer.name}
          iri={linkData.value}
          isHighlighted={isLabelModified}
          label={label}
          onSetIri={handleUpdateIri}
          onSetLabel={handleUpdateLabel}
          placeholder={
            label === ''
              ? copy.components.projectDiagram.modelConceptRelationship.new
              : ''
          }
          testId={TEST_IDS.projectDiagram.entityDrawer.name}
        />
        <ProjectDiagramEditableTextArea
          // key here to description changes when you change from drawer to drawer
          key={`${linkData.value}-description`}
          header={copy.components.projectDiagram.classDrawer.description}
          isHighlighted={isDescriptionModified}
          onSetValue={handleUpdateDescription}
          value={descriptionForIri}
        />

        {isCustomPropertyType ? (
          <PropertyTypeSelector
            debounced
            propertyType={propertyType}
            setPropertyType={handleUpdatePropertyType}
          />
        ) : null}

        <div className="sd-drawer-project-diagram-entity-columns">
          <SelectableLinks
            buttonText=""
            creatable
            createNewItemFromQuery={createNewClassFromQuery}
            createNewItemRenderer={createNewSelectItemRenderer}
            createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
              ''
            )}
            filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
            header={copy.components.projectDiagram.relationshipDrawer.domain}
            highlightedValues={addedSources}
            items={domainAndRangeSelectItems.availableDomains}
            itemTestId={(value, idx) =>
              getProjectDiagramEntityDrawerSelectLinkTestId(
                value,
                PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE,
                idx
              )
            }
            onClick={handleClickClass}
            onRemove={handleRemoveDomain}
            onSelect={handleAddDomain}
            selected={domainAndRangeSelectItems.currentDomains}
            selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
            selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
              PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE
            )}
          />
          <SelectableLinks
            buttonText=""
            creatable
            createNewItemFromQuery={createNewClassFromQuery}
            createNewItemRenderer={createNewSelectItemRenderer}
            createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
              ''
            )}
            filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
            header={copy.components.projectDiagram.relationshipDrawer.range}
            highlightedValues={addedTargets}
            items={domainAndRangeSelectItems.availableRanges}
            itemTestId={(value, idx) =>
              getProjectDiagramEntityDrawerSelectLinkTestId(
                value,
                PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE,
                idx
              )
            }
            onClick={handleClickClass}
            onRemove={handleRemoveRange}
            onSelect={handleAddRange}
            selected={domainAndRangeSelectItems.currentRanges}
            selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
            selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
              PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE
            )}
          />
        </div>

        <ProjectDiagramRelationshipDrawerTable
          domains={domainAndRangeSelectItems.currentDomains}
          highlightedDomains={addedSources}
          highlightedRanges={addedTargets}
          onClick={handleClickClass}
          ranges={domainAndRangeSelectItems.currentRanges}
        />
      </div>

      <div className={Classes.DRAWER_FOOTER}>
        <div className="sd-drawer-footer-actions">
          <Button
            data-testid={TEST_IDS.projectDiagram.entityDrawer.removeButton}
            onClick={() => handleRemoveRelationship({ key: linkData.key })}
            outlined
          >
            {copy.components.projectDiagram.entityDrawerButtons.remove}
          </Button>
        </div>
      </div>
    </>
  );
};
